import clsx from 'clsx'
import { useEffect, useState, useRef } from 'react'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'

import { ReactComponent as ChevronLeft } from 'assets/icons/chevron-left.svg'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import { Icon } from 'components/common/Icon/Icon'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import SliderItem from './sliderItem/SliderItem'
import { VideoData } from './videoData'
import styles from './VideoSlider.module.scss'
import { Flex } from '../common/flex/Flex'
import VideoModal from '../common/videoModal/VideoModal'

const VideoSlider = () => {
  const [slidesPerView, setSlidesPerView] = useState(3)

  useEffect(() => {
    const updateSlidesPerView = () => {
      setSlidesPerView(window.innerWidth <= 767 ? 1 : 3)
    }

    updateSlidesPerView()
    window.addEventListener('resize', updateSlidesPerView)

    return () => window.removeEventListener('resize', updateSlidesPerView)
  }, [])

  let slides = VideoData
  while (slides.length <= slidesPerView) {
    slides = [...slides, ...VideoData]
  }

  const [firstVisibleSlideIndex, setFirstVisibleSlideIndex] = useState(0)
  const [currentVideo, setCurrentVideo] = useState<string | null>(null)
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false)
  const [playVideo, setPlayVideo] = useState(false)
  const [arrowContainerStyle, setArrowContainerStyle] = useState('92px')
  const swiperRef = useRef<SwiperRef>(null)

  const handleSlideClick = (direction: 'next' | 'prev') => {
    if (swiperRef.current) {
      if (direction === 'next') {
        swiperRef.current.swiper.slideNext()
        trackAnalytics({
          type: AnalyticsActionType.action,
          payload: EVENTS.INNOVATION_CAROUSEL.RIGHT,
        })
      } else {
        swiperRef.current.swiper.slidePrev()
        trackAnalytics({
          type: AnalyticsActionType.action,
          payload: EVENTS.INNOVATION_CAROUSEL.LEFT,
        })
      }
    }
  }

  const onPlay = (videoUrl: string) => {
    setCurrentVideo(videoUrl)
    setIsOpenVideoModal(true)
    setPlayVideo(true)
  }

  function arrowContainerPosition(index: number) {
    setArrowContainerStyle(index === 2 || index === 5 ? '81px' : '92px')
  }

  return (
    <>
      <Swiper
        ref={swiperRef}
        speed={500}
        slidesPerView={slidesPerView}
        spaceBetween={200}
        loop={true}
        navigation={{
          nextEl: '#swiperButtonNext',
          prevEl: '#swiperButtonPrev',
        }}
        allowTouchMove={false}
        modules={[Navigation]}
        className={styles.sliderContainer}
        onSlideChangeTransitionStart={({ realIndex }) => {
          setFirstVisibleSlideIndex(realIndex)
          arrowContainerPosition(realIndex)
        }}
      >
        {slides.map((video, index) => {
          const isLeftSlide = index === firstVisibleSlideIndex
          const isCenterSlide = index === (firstVisibleSlideIndex + Math.floor(slidesPerView / 2)) % slides.length
          const isRightSlide = index === (firstVisibleSlideIndex + slidesPerView - 1) % slides.length
          const isHiddenSlide = !isLeftSlide && !isCenterSlide && !isRightSlide

          return (
            <SwiperSlide
              key={index}
              className={clsx(styles.slideItem, {
                [styles.slideItemHidden]: isHiddenSlide,
              })}
            >
              <SliderItem
                img={video.img}
                title={video.title}
                isCenterSlide={isCenterSlide}
                onPlay={() => {
                  onPlay(video.videoUrl)
                  trackAnalytics({
                    type: AnalyticsActionType.action,
                    payload: video.event,
                  })
                }}
                onClick={() => {
                  if (isLeftSlide) handleSlideClick('prev')
                  else if (isRightSlide) handleSlideClick('next')
                }}
              />
            </SwiperSlide>
          )
        })}
        <Flex className={styles.sliderArrowsContainer} gap={520} style={{ bottom: arrowContainerStyle }}>
          <button
            id="swiperButtonPrev"
            className={styles.hiddenButton}
            onClick={() => {
              handleSlideClick('prev')
            }}
          >
            <Icon icon={ChevronLeft} size={48} color="#000000" className={styles.sliderArrow} />
          </button>
          <button
            id="swiperButtonNext"
            className={styles.hiddenButton}
            onClick={() => {
              handleSlideClick('next')
            }}
          >
            <Icon icon={ChevronRight} size={48} color="#000000" className={styles.sliderArrow} />
          </button>
        </Flex>
      </Swiper>
      <VideoModal
        show={isOpenVideoModal}
        setShow={setIsOpenVideoModal}
        videoSrc={currentVideo || ''}
        playVideo={playVideo}
        setPlayVideo={setPlayVideo}
      />
    </>
  )
}

export default VideoSlider
